/**
 * SPORT SPEAKER — Console du speaker (Fabrice).
 * Accès en lecture seule pour annoncer les joueurs sur le micro stade.
 *
 * Features :
 *  - Login PIN (X-Match-Speaker-Pin)
 *  - Match live actuel (score, chrono — read-only)
 *  - Recherche joueurs IPHB + Adverse
 *  - Click joueur → fiche complète (palmarès, parcours)
 *  - Tabs IPHB / Adverse / Tous
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowLeft, Mic } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import Flag from "../../components/sport/Flag";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DEEP = "#0D0220";
const PIN_KEY = "iphb_speaker_pin";

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }

function useTick(ms = 1000) {
  const [, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT((x) => x + 1), ms); return () => clearInterval(id); }, [ms]);
}

function PinLogin({ onAuth }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.get("/match-live/speaker/check", { headers: { "X-Match-Speaker-Pin": pin } });
      if (r.data?.ok) {
        localStorage.setItem(PIN_KEY, pin);
        onAuth(pin);
        toast.success("Bienvenue, speaker !");
      } else { toast.error("PIN incorrect"); }
    } catch { toast.error("Erreur de connexion"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: `radial-gradient(circle at top, ${PRIMARY}, ${BG_DEEP})` }}>
      <form onSubmit={submit} className="w-full max-w-sm bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-md" data-testid="speaker-pin-form">
        <Mic size={28} className="text-pink-400 mb-3"/>
        <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Accès Speaker</div>
        <h1 className="text-3xl font-blackhawk italic uppercase mb-1 text-white">Console Stade</h1>
        <p className="text-white/60 text-sm mb-6">Annoncez les joueurs depuis votre tablette pendant le match.</p>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="••••"
          className="w-full px-4 py-3 rounded-lg bg-black/40 border border-white/15 text-white text-xl tracking-widest text-center focus:outline-none focus:border-pink-400"
          data-testid="speaker-pin-input"
        />
        <button type="submit" disabled={busy || !pin}
          className="w-full mt-4 py-3 rounded-lg font-bold uppercase tracking-wider text-white disabled:opacity-50 transition"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
          data-testid="speaker-pin-submit">
          {busy ? "Vérification..." : "Entrer"}
        </button>
        <Link to="/sport" className="block mt-4 text-center text-xs text-white/40 hover:text-white">← Retour au site</Link>
      </form>
    </div>
  );
}

function LiveTicker({ session }) {
  useTick(1000);
  if (!session) {
    return (
      <div className="rounded-2xl px-4 py-3 bg-white/5 border border-white/10 text-center" data-testid="speaker-ticker">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Aucun match en cours</div>
        <div className="text-white/70 text-sm mt-1">L'opérateur n'a pas encore lancé la session</div>
      </div>
    );
  }
  const startMs = session.chrono_started_at ? new Date(session.chrono_started_at).getTime() : null;
  let label = "--:--";
  if (startMs) {
    const totalPaused = session.total_paused_ms || 0;
    let endRef;
    if (session.status === "halftime" && session.chrono_paused_at) endRef = new Date(session.chrono_paused_at).getTime();
    else if (session.status === "finished") endRef = session.finished_at ? new Date(session.finished_at).getTime() : Date.now();
    else endRef = Date.now();
    const sec = Math.floor(Math.max(0, endRef - startMs - totalPaused) / 1000);
    label = `${pad(Math.floor(sec / 60))}:${pad(sec % 60)}`;
  }
  return (
    <div className="rounded-2xl px-4 py-3 bg-white/5 border border-white/10 grid grid-cols-3 items-center" data-testid="speaker-ticker">
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.3em] text-white/50 truncate">{session.home_team}</div>
        <div className="text-3xl font-blackhawk text-white tabular-nums">{session.home_score}</div>
      </div>
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.3em] text-pink-300 mb-1">
          {session.status === "halftime" ? "Mi-temps" : session.status === "finished" ? "Terminé" : `${session.period}ʳᵉ MT`}
        </div>
        <div className="text-2xl font-bebas text-white tabular-nums">{label}</div>
      </div>
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.3em] text-white/50 truncate">{session.away_team}</div>
        <div className="text-3xl font-blackhawk text-white tabular-nums">{session.away_score}</div>
      </div>
    </div>
  );
}

function PlayerTile({ player, onClick }) {
  const number = player.jersey_number ?? "—";
  const name = (player.name || `${player.first_name || ""} ${player.last_name || ""}`).trim();
  return (
    <button onClick={onClick} data-testid={`speaker-tile-${player.slug || player.id}`}
      className="group relative rounded-xl overflow-hidden border border-white/10 hover:border-pink-400 transition bg-gradient-to-b from-white/5 to-white/0 text-left">
      <div className="aspect-[3/4] overflow-hidden bg-black/40 relative">
        {player.photo_url && (
          <img src={player.photo_url} alt={name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }}/>
        )}
        <div className="absolute top-1.5 right-1.5">
          <Flag code={player.country_code} emoji={player.country_flag_emoji} size={16} className="ring-1 ring-white/40"/>
        </div>
        <div className="absolute bottom-1 left-1.5 font-bebas font-black tabular-nums text-pink-300 text-2xl leading-none drop-shadow-lg">{number}</div>
      </div>
      <div className="p-1.5">
        <div className="text-white font-bold uppercase truncate text-[11px]">{name}</div>
        <div className="text-white/50 text-[9px] uppercase tracking-wider truncate">{player.position || ""}</div>
      </div>
    </button>
  );
}

function PlayerDetailDialog({ slug, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/sport/player/${slug}`)
      .then((r) => setData(r.data?.player || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto" style={{ background: "rgba(5,0,16,0.95)", backdropFilter: "blur(8px)" }}>
      <div className="min-h-full flex flex-col">
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ background: "rgba(13,2,32,0.95)" }}>
          <div className="font-blackhawk italic uppercase text-white text-lg">{data?.name || "Fiche joueur"}</div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-2xl leading-none w-9 h-9 flex items-center justify-center" data-testid="speaker-detail-close">×</button>
        </div>
        <div className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full">
          {loading && <div className="text-white/50 italic text-center py-12">Chargement…</div>}
          {!loading && !data && <div className="text-white/50 italic text-center py-12">Fiche introuvable</div>}
          {data && (
            <div data-testid="speaker-player-detail">
              <div className="rounded-2xl overflow-hidden mb-4"
                style={{ background: `linear-gradient(160deg, ${PRIMARY} 0%, ${BG_DEEP} 70%)`, border: "1px solid rgba(255,255,255,0.1)" }}>
                <div className="grid grid-cols-[auto,1fr] gap-3 p-5">
                  <div className="font-bebas font-black tabular-nums leading-none italic"
                    style={{ fontSize: "clamp(4rem, 14vw, 7rem)", color: "#fff", textShadow: `0 0 22px ${SECONDARY}88`, WebkitTextStroke: `2px ${SECONDARY}` }}>
                    {data.jersey_number ?? "—"}
                  </div>
                  {data.photo_url && (
                    <div className="self-end justify-self-end w-32 h-32 rounded-xl overflow-hidden ring-2 ring-pink-400/40">
                      <img src={data.photo_url} alt={data.name} className="w-full h-full object-cover object-top"/>
                    </div>
                  )}
                </div>
                <div className="px-5 pb-5">
                  <div className="text-pink-300 text-[11px] uppercase tracking-[0.3em] font-bold">{data.position}</div>
                  <div className="text-white font-blackhawk italic uppercase leading-tight" style={{ fontSize: "clamp(1.6rem, 5vw, 2.2rem)" }}>{data.name}</div>
                  {(data.country_code || data.country_flag_emoji) && (
                    <div className="mt-2 flex items-center gap-2">
                      <Flag code={data.country_code} emoji={data.country_flag_emoji} size={26} label={data.country || ""}/>
                      <span className="text-white/70 text-sm">{data.country || ""}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                {data.age && <div className="rounded-lg p-3 bg-white/5 border border-white/10"><div className="text-[10px] text-white/50 uppercase">Âge</div><div className="text-white font-bold">{data.age} ans</div></div>}
                {data.birthdate && <div className="rounded-lg p-3 bg-white/5 border border-white/10"><div className="text-[10px] text-white/50 uppercase">Né le</div><div className="text-white font-bold">{data.birthdate}</div></div>}
                {data.height_cm && <div className="rounded-lg p-3 bg-white/5 border border-white/10"><div className="text-[10px] text-white/50 uppercase">Taille</div><div className="text-white font-bold">{data.height_cm} cm</div></div>}
                {data.weight_kg && <div className="rounded-lg p-3 bg-white/5 border border-white/10"><div className="text-[10px] text-white/50 uppercase">Poids</div><div className="text-white font-bold">{data.weight_kg} kg</div></div>}
                {data.team && <div className="col-span-2 rounded-lg p-3 bg-white/5 border border-white/10 flex items-center gap-2"><div className="text-[10px] text-white/50 uppercase">Club</div>{data.team_logo && <img src={data.team_logo} alt="" className="h-5 w-5 object-contain"/>}<div className="text-white font-bold">{data.team}</div></div>}
              </div>

              {data.career?.length > 0 && (
                <div className="mt-5">
                  <div className="text-pink-400 font-black tracking-[0.3em] text-[11px] uppercase mb-2">Parcours</div>
                  <div className="space-y-1">
                    {data.career.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm">
                        <span className="text-pink-300 text-xs font-bold tabular-nums w-20">{c.season || c.years || ""}</span>
                        <span className="text-white truncate">{c.club || c.team || c.label || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SportSpeaker() {
  const [pin, setPin] = useState(() => localStorage.getItem(PIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [nextMatch, setNextMatch] = useState(null);
  const [activeSlug, setActiveSlug] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    if (!pin) { setLoading(false); return; }
    api.get("/match-live/speaker/check", { headers: { "X-Match-Speaker-Pin": pin } })
      .then((r) => { if (r.data?.ok) setAuthed(true); else { localStorage.removeItem(PIN_KEY); setPin(""); } })
      .catch(() => { localStorage.removeItem(PIN_KEY); setPin(""); })
      .finally(() => setLoading(false));
  }, [pin]);

  // Polling session live
  useEffect(() => {
    if (!authed) return;
    const fetchS = () => api.get("/match-live/active").then((r) => setSession(r.data?.session || null)).catch(() => {});
    fetchS();
    const id = setInterval(fetchS, 4000);
    return () => clearInterval(id);
  }, [authed]);

  // Load home roster + next match
  useEffect(() => {
    if (!authed) return;
    api.get("/sport/team-info").then((r) => {
      const list = (((r.data || {}).info || {}).players || []);
      setPlayers(list);
    }).catch(() => {});
    api.get("/sport/next-match").then((r) => setNextMatch(r.data?.match || null)).catch(() => {});
  }, [authed]);

  // Load away roster
  useEffect(() => {
    if (!authed) return;
    let oppName = null;
    if (session?.away_team && !/istres|iphb/i.test(session.away_team)) oppName = session.away_team;
    else if (session?.home_team && !/istres|iphb/i.test(session.home_team)) oppName = session.home_team;
    else if (nextMatch) {
      const isHome = /istres|iphb/i.test(nextMatch.home_team || "");
      oppName = isHome ? nextMatch.away_team : nextMatch.home_team;
    }
    if (!oppName) return;
    const slug = oppName.toLowerCase().replace(/\s+/g, "-");
    api.get(`/sport/team-roster?slug=${encodeURIComponent(slug)}`).then((r) => setAwayPlayers(r.data?.players || [])).catch(() => {});
  }, [authed, session, nextMatch]);

  const all = useMemo(() => {
    const home = (players || []).filter((p) => p.last_name).map((p) => ({ ...p, _side: "home", _team: "IPHB" }));
    const away = (awayPlayers || []).filter((p) => p.name || p.last_name).map((p) => ({ ...p, _side: "away", _team: "Adv." }));
    return [...home, ...away];
  }, [players, awayPlayers]);
  const counts = { all: all.length, home: all.filter((p) => p._side === "home").length, away: all.filter((p) => p._side === "away").length };
  const filtered = useMemo(() => {
    let pool = all;
    if (tab === "home") pool = pool.filter((p) => p._side === "home");
    if (tab === "away") pool = pool.filter((p) => p._side === "away");
    const needle = q.trim().toLowerCase();
    if (needle) pool = pool.filter((p) => {
      const n = (p.name || `${p.first_name || ""} ${p.last_name || ""}`).toLowerCase();
      return n.includes(needle) || String(p.jersey_number || "").includes(needle);
    });
    return pool;
  }, [all, q, tab]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60" style={{ background: PRIMARY }}>Chargement…</div>;
  if (!authed) return <PinLogin onAuth={(p) => { setPin(p); setAuthed(true); }}/>;

  const TabBtn = ({ id, label, n }) => (
    <button onClick={() => setTab(id)} data-testid={`speaker-tab-${id}`}
      className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold transition ${tab === id ? "text-white" : "text-white/50 hover:text-white"}`}
      style={tab === id ? { background: SECONDARY, boxShadow: `0 6px 16px ${SECONDARY}55` } : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
      {label} <span className="opacity-60 ml-1">({n})</span>
    </button>
  );

  return (
    <div className="min-h-screen text-white" style={{ background: `radial-gradient(circle at top, ${PRIMARY}, ${BG_DEEP})` }} data-testid="speaker-root">
      <header className="sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ background: "rgba(13,2,32,0.95)", backdropFilter: "blur(8px)" }}>
        <Link to="/sport" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm">
          <ArrowLeft size={16}/> Site
        </Link>
        <div className="inline-flex items-center gap-2">
          <Mic size={16} className="text-pink-400"/>
          <span className="font-blackhawk italic uppercase text-white tracking-wider">Console Speaker</span>
        </div>
        <button onClick={() => { localStorage.removeItem(PIN_KEY); setAuthed(false); setPin(""); }}
          className="text-xs text-white/50 hover:text-white" data-testid="speaker-logout">Déconnexion</button>
      </header>

      <div className="px-4 py-4 max-w-3xl mx-auto">
        {/* Live ticker */}
        <LiveTicker session={session}/>

        {/* Search bar + tabs */}
        <div className="mt-5 mb-3 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher : nom, prénom ou numéro…"
            data-testid="speaker-search-input"
            className="w-full pl-9 pr-4 py-3 rounded-full bg-white/10 text-white placeholder:text-white/40 border border-white/15 focus:outline-none focus:border-pink-400"
          />
        </div>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <TabBtn id="all" label="Tous" n={counts.all}/>
          <TabBtn id="home" label="IPHB" n={counts.home}/>
          <TabBtn id="away" label="Adverses" n={counts.away}/>
        </div>

        {/* Players grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="speaker-grid">
          {filtered.map((p) => (
            <PlayerTile key={`${p._side}-${p.slug || p.id}`} player={p} onClick={() => setActiveSlug(p.slug)}/>
          ))}
        </div>
        {filtered.length === 0 && <div className="text-white/50 italic text-center py-10">Aucun joueur trouvé</div>}
      </div>

      {activeSlug && <PlayerDetailDialog slug={activeSlug} onClose={() => setActiveSlug(null)}/>}
    </div>
  );
}
